import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Undo2 } from "lucide-react";
import {
    FocusLayout,
    GridLayout,
    ParticipantTile,
    RoomAudioRenderer,
    useRemoteParticipants,
    useTracks,
} from "@livekit/components-react";
import { Track } from "livekit-client";

import { useActiveSpeaker } from "../../application/useActiveSpeaker";
import { useCompactRoom } from "../../application/useCompactRoom";
import { useFullscreen } from "../../application/useFullscreen";
import { useGongChannel } from "../../application/useGongChannel";
import { useMeetingChat } from "../../application/useMeetingChat";
import { useMeetingLayout } from "../../application/useMeetingLayout";
import { planStage } from "../../domain/meetingLayout";
import { useWhiteboardChannel } from "../../application/useWhiteboardChannel";
import { useToolBoardChannel } from "../../application/useToolBoardChannel";
import type { TherapistToolBoard } from "../../domain/meetingToolBoard";
import { mayClearBoard } from "../../domain/whiteboard";
import { useMeditationChannel, MeetingMeditation } from "../../../meditation";
import { BlsControlPanel, PatientBlsView, useBlsChannel, useBlsHaptics } from "../../../emdr";
import type { EmdrSetRecorder } from "../../../emdr";
import MeetingAudioBadge from "./MeetingAudioBadge";
import MeetingBar from "./MeetingBar";
import MeetingChat from "./MeetingChat";
import MeetingGongCountdown from "./MeetingGongCountdown";
import MeetingToolBoard from "./MeetingToolBoard";
import Whiteboard from "./Whiteboard";

/**
 * What a track is called when it has to be named across renders: pinning
 * remembers a choice about one publication, and a participant sharing a
 * screen has two.
 */
function trackKey(track: { participant: { identity: string }; source: string }): string {
    return `${track.participant.identity}:${track.source}`;
}

/**
 * The call itself: the participants, and the controls under them.
 *
 * Assembled from LiveKit's primitives rather than using their prebuilt
 * conference, whose labels are hardcoded English — "Camera", "Share screen",
 * "Leave" — with no way to translate them. A Spanish therapist should not be
 * reading English at the one moment they are least able to think about it.
 *
 * `canLeave` is off where the page already owns the ending — the therapist's
 * session page finishes the session for both people, and a second, quieter
 * button that only drops the connection would be the wrong one to reach for.
 *
 * The whiteboard replaces the tiles rather than sitting beside them: on a
 * laptop, two faces and a drawing surface at once leaves the drawing surface
 * too small to draw on. Audio keeps running, so the conversation does not stop
 * while the board is up.
 *
 * The board's contents live here rather than inside the panel, because the
 * panel comes and goes every time someone wants to see a face. Holding the
 * strokes at this level means glancing back at the video and returning finds
 * the drawing exactly where it was, even if the other person closed their
 * panel too.
 *
 * Whether the board is *up* lives one level further down still, in the
 * channel: raising it is something one person does to the call, and it used to
 * be local state here. The result was a therapist drawing on a board the
 * patient was never shown, which both of them read as strokes that would not
 * sync.
 *
 * The chat sits beside the video rather than replacing it — a sentence does
 * not need the width a drawing does — except on a narrow screen, where there
 * is only room for one of the two.
 *
 * A meditation takes the same slot as the board, for the same reason: the
 * therapist needs the width for the script they are reading. It differs in who
 * decides it is up. The therapist chooses, and can put it away to look at the
 * patient while the audio keeps playing; the patient never chooses — theirs
 * appears when the therapist starts one and goes when it ends, because a
 * patient with their eyes closed is not going to press anything, and a control
 * on their screen is only an invitation to open them.
 *
 * Bilateral stimulation splits the two screens the other way round. The
 * therapist's controls sit in a column beside the video, because watching the
 * patient's eyes is the therapist's whole job during a set. The patient's
 * stimulus takes the stage, edge to edge, because the dot needs the width —
 * and, like a meditation, it appears when the therapist starts a set and goes
 * when they end it.
 *
 * A tool's board — the constellation, or the genogram and ecomap — takes the
 * stage too, each side framing its own page of it: the therapist their board,
 * the patient the one their link opens. The faces stay beside it rather than
 * giving way to it, because watching what the board does to the person
 * building it is the reason for building it in a call.
 *
 * On a phone the same stage is drawn for a screen with no room to spare: the
 * call can take the whole of it, the controls become icons on one row, and
 * the picture opens on the other person with you in the corner rather than on
 * a grid of two stamps. The corner tiles are the way back — pressing one puts
 * that person on the stage and holds them there, which is how you keep
 * watching the partner who is *not* talking.
 */
type MeetingStageProps = {
    canLeave?: boolean;
    /** Clearing and saving belong to the therapist, not to the patient. */
    canManageBoard?: boolean;
    onSaveBoard?: (imageBase64: string) => Promise<void>;
    /**
     * Whether the meditation panel is up, when something outside the room
     * decides — the session page's tools panel, which lists the meditation
     * library beside the other adopted tools and sits outside the LiveKit
     * context. Left out, the stage keeps the state itself.
     */
    meditationOpen?: boolean;
    onMeditationOpenChange?: (open: boolean) => void;
    /**
     * Whether the bilateral stimulation panel is up, when the session page's
     * tools panel decides — for the same reason as the meditation's.
     */
    bilateralOpen?: boolean;
    onBilateralOpenChange?: (open: boolean) => void;
    /**
     * Saves a set's SUD/VOC against the session, over REST from the
     * therapist's page. Absent on the patient's page and on sessions that are
     * not one patient's.
     */
    emdrRecorder?: EmdrSetRecorder;
    /**
     * The tool board the therapist put up from the session page's tools
     * panel, which sits outside the room. Absent on the patient's page, whose
     * board arrives over the data channel.
     */
    toolBoard?: TherapistToolBoard | null;
    /** Put the tool board away, for everyone. */
    onToolBoardClose?: () => void;
};

export default function MeetingStage({
    canLeave = true,
    canManageBoard = false,
    onSaveBoard,
    meditationOpen: controlledOpen,
    onMeditationOpenChange,
    bilateralOpen: controlledBilateralOpen,
    onBilateralOpenChange,
    emdrRecorder,
    toolBoard: therapistToolBoard = null,
    onToolBoardClose,
}: MeetingStageProps) {
    const { t } = useTranslation();
    const board = useWhiteboardChannel();
    const meditation = useMeditationChannel();
    /* Held here so a set keeps running while the panel comes and goes. */
    const bls = useBlsChannel();
    const chat = useMeetingChat();
    const toolBoard = useToolBoardChannel(therapistToolBoard);
    /*
     * Held here rather than in the bar: the player owns an audio context, and
     * a gong that arrives while the board is up still has to sound.
     */
    const gong = useGongChannel();

    /**
     * Whether the meditation panel is on this screen.
     *
     * Local to the therapist — putting it away is a private choice, the same
     * as closing the board — while the patient's follows whether one is
     * running at all.
     */
    const [ownOpen, setOwnOpen] = useState(false);
    const meditationOpen = controlledOpen ?? ownOpen;
    const setMeditationOpen = (open: boolean) => {
        setOwnOpen(open);
        onMeditationOpenChange?.(open);
    };
    const showMeditation = meditation.canControl
        ? meditationOpen
        : meditation.state !== null;
    const [ownBilateralOpen, setOwnBilateralOpen] = useState(false);
    const bilateralOpen = controlledBilateralOpen ?? ownBilateralOpen;
    const setBilateralOpen = (open: boolean) => {
        setOwnBilateralOpen(open);
        onBilateralOpenChange?.(open);
    };
    const showBilateralPanel = bls.canControl && bilateralOpen;
    /*
     * The panel is the only place a set can be stopped from, so a set never
     * outlives it — whoever closes it, including the page putting it away to
     * make room for the meditation.
     */
    const { canControl: blsCanControl, state: blsState, stop: stopBls } = bls;
    useEffect(() => {
        if (blsCanControl && !bilateralOpen && blsState) stopBls();
    }, [bilateralOpen, blsCanControl, blsState, stopBls]);
    /*
     * The patient's controllers, watched for the whole call rather than only
     * during a set, so the therapist knows before starting one whether tactile
     * will reach anybody's hands.
     */
    const haptics = useBlsHaptics({ state: blsState, cyclesNow: bls.cyclesNow, enabled: !blsCanControl });
    const { reportControllers } = bls;
    useEffect(() => {
        if (!blsCanControl) reportControllers(haptics.controllers);
    }, [blsCanControl, haptics.controllers, reportControllers]);
    const showPatientBilateral = !bls.canControl && bls.state !== null;
    /*
     * Whether one is actually under way, as opposed to being looked for. The
     * two are different screens: the library takes the stage, the meditation
     * itself hands it back to the video.
     */
    const [meditationRunning, setMeditationRunning] = useState(false);
    const remoteParticipants = useRemoteParticipants();

    /** A screen with no room to spare: a phone, in either orientation. */
    const compact = useCompactRoom();

    /**
     * What the call fills when somebody asks for the whole screen.
     *
     * The stage rather than the page: the header, the therapist's dock and
     * the page's own padding are exactly what a phone has no room for, and
     * leaving them out of the fullscreen element is what removes them.
     */
    const stageRef = useRef<HTMLDivElement | null>(null);
    const fullscreen = useFullscreen(stageRef);

    // Who may wipe the board follows from the identity the meeting service
    // minted, not from a prop, so the control and the rule that enforces it
    // on the receiving side can never disagree.
    const canClearBoard = mayClearBoard(board.localIdentity);

    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: false }
    );

    /** Everybody's camera, kept beside a tool's board while it is up. */
    const faces = useMemo(
        () => tracks.filter((track) => track.source === Track.Source.Camera),
        [tracks]
    );

    /**
     * The screen somebody is sharing, if anybody is.
     *
     * Theirs before yours: your own screen is already in front of you, and a
     * therapist who shares while the patient is sharing is looking at what the
     * patient put up, not at their own desktop again.
     */
    const sharedScreen = useMemo(() => {
        const shares = tracks.filter(
            (track) => track.source === Track.Source.ScreenShare
        );
        return shares.find((track) => !track.participant.isLocal) ?? shares[0] ?? null;
    }, [tracks]);

    /**
     * Who is big, who is small — and, while a screen is up, decided by the
     * share rather than by whatever this person normally watches calls in.
     */
    const layout = useMeetingLayout(sharedScreen !== null, compact);

    /** Who the room is listening to, when nobody has pinned anybody. */
    const speaker = useActiveSpeaker();

    /**
     * Whoever somebody asked to keep on the stage, as `identity:source`.
     *
     * Local and deliberately not remembered: pinning is something you do
     * inside one conversation — "stay on her while he reads that out" — and
     * a pin that survived into next week's session would be a picture stuck
     * on somebody who may not even be in the room.
     */
    const [pinnedKey, setPinnedKey] = useState<string | null>(null);

    /**
     * Whether anyone else is here yet.
     *
     * A patient who opens their link early used to sit alone in a grid beside
     * an empty placeholder, with nothing on screen saying the therapist had not
     * arrived — which reads as a call that failed rather than one that has not
     * started.
     */
    const alone = remoteParticipants.length === 0;

    /**
     * Who is big, who is small, who is not shown.
     *
     * The decision itself is a pure function in the domain, so the rules — a
     * shared screen outranks a face, you are never a thumbnail over yourself —
     * can be read and tested without a room.
     */
    const plan = useMemo(
        () =>
            planStage(
                layout.presented,
                tracks.map((track) => ({
                    track,
                    key: trackKey(track),
                    isLocal: track.participant.isLocal,
                    isScreenShare: track.source === Track.Source.ScreenShare,
                    isSpeaking: track.participant.identity === speaker,
                    isPinned: trackKey(track) === pinnedKey,
                }))
            ),
        [layout.presented, pinnedKey, speaker, tracks]
    );

    /**
     * Whether the stage is being held there by hand.
     *
     * Read back off the plan rather than off the state that asked for it: a
     * pinned participant who leaves the call takes their track with them, the
     * stage falls back to whoever is talking, and a control still offering to
     * "go back to automatic" would be offering to undo nothing.
     */
    const held = plan.stage?.isPinned === true;

    return (
        <div
            ref={stageRef}
            data-testid="meeting-stage"
            data-fullscreen={fullscreen.active ? "true" : undefined}
            /*
             * Pinned over the page when the browser would not give us its own
             * fullscreen — an iPhone, where Safari offers it to <video>
             * elements only and a tile is a div. `bg-room-base` because a
             * fullscreen element defaults to a black box, and `p-2` so the
             * call is not flush against the bezel.
             */
            className={`flex min-h-0 flex-col ${
                fullscreen.active
                    ? "gap-2 bg-room-base p-2 " +
                      (fullscreen.native
                          ? "h-full"
                          : // `dvh` rather than `inset-0`, which on iOS is the
                            // layout viewport and reaches under Safari's own
                            // toolbar — putting the leave button back where
                            // this pass found it.
                            "fixed inset-x-0 top-0 z-50 h-[100dvh]")
                    : "h-full gap-3"
            }`}
        >
            <div className={`flex min-h-0 flex-1 ${compact ? "gap-2" : "gap-3"}`}>
                {/*
                  * `relative` so the gong's countdown can sit in the corner of
                  * whatever is showing. It hangs here rather than inside the
                  * video, because a sequence keeps running while the board is
                  * up and the silence is exactly when it is being drawn on.
                  */}
                <div
                    className={`relative min-h-0 flex-1 flex-col ${
                        chat.open ? "hidden sm:flex" : "flex"
                    }`}
                >
                    <MeetingGongCountdown channel={gong} />

                    {/*
                      * A share rearranges the picture by itself, which a
                      * sighted participant takes in at a glance and a screen
                      * reader user would otherwise never be told about: the
                      * participant names it reads out do not change. Not shown,
                      * because on screen the shared document says it better
                      * than a line of text could.
                      *
                      * Always in the document, filled and emptied rather than
                      * mounted and unmounted: a live region that arrives with
                      * its text already in it is announced by some screen
                      * readers and silently ignored by others.
                      */}
                    <p role="status" className="sr-only" data-testid="share-notice">
                        {sharedScreen
                            ? t("meetingLayout.sharing", {
                                  name:
                                      sharedScreen.participant.name ||
                                      sharedScreen.participant.identity ||
                                      t("meetingLayout.someone"),
                              })
                            : ""}
                    </p>

                    {showPatientBilateral ? (
                        <div className="min-h-0 flex-1">
                            <PatientBlsView channel={bls} haptics={haptics} />
                        </div>
                    ) : toolBoard.frameSrc && toolBoard.kind ? (
                        <div className="min-h-0 flex-1">
                            <MeetingToolBoard
                                kind={toolBoard.kind}
                                src={toolBoard.frameSrc}
                                faces={faces}
                                speaker={speaker}
                                onClose={toolBoard.canControl ? onToolBoardClose : undefined}
                                onEnded={toolBoard.canControl ? onToolBoardClose : toolBoard.dismiss}
                            />
                        </div>
                    ) : board.open ? (
                        <div className="min-h-0 flex-1">
                            <Whiteboard
                                channel={board}
                                canClear={canClearBoard}
                                onSave={canManageBoard ? onSaveBoard : undefined}
                            />
                        </div>
                    ) : (
                        <div className="relative min-h-0 flex-1">
                            {plan.grid ? (
                                <GridLayout
                                    tracks={plan.grid.map((entry) => entry.track)}
                                    className="h-full"
                                >
                                    <ParticipantTile />
                                </GridLayout>
                            ) : (
                                <>
                                    {plan.stage && (
                                        <FocusLayout
                                            trackRef={plan.stage.track}
                                            className="h-full"
                                        />
                                    )}

                                    {/*
                                      * The way out of a pin, and the only
                                      * thing on screen saying the picture has
                                      * stopped following the conversation.
                                      * Top left, away from the tiles, and
                                      * gone the moment the pin is.
                                      */}
                                    {held && (
                                        <button
                                            type="button"
                                            onClick={() => setPinnedKey(null)}
                                            data-testid="unpin"
                                            /* Icon alone on a phone: spelled
                                               out it is half the width of the
                                               screen, and it would sit across
                                               the badge that says somebody is
                                               sharing audio. */
                                            className={`absolute left-3 top-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-room-line bg-room-base/85 text-xs font-medium text-room-ink backdrop-blur-sm hover:bg-room-hover ${
                                                compact ? "w-11 px-0" : "px-3"
                                            }`}
                                        >
                                            <Undo2 size={14} aria-hidden="true" />
                                            <span
                                                className={
                                                    compact ? "sr-only" : undefined
                                                }
                                            >
                                                {t("meetingLayout.unpin")}
                                            </span>
                                        </button>
                                    )}

                                    {plan.rail.length > 0 && (
                                        // Bottom right, over the stage, the way
                                        // every other call product puts it — and
                                        // small enough that whatever is being
                                        // pointed at stays the thing on screen.
                                        //
                                        // A row rather than the single tile it
                                        // used to be: behind a shared screen
                                        // every face has to go somewhere, and a
                                        // couple session can hold more than two
                                        // of them. It scrolls rather than
                                        // shrinking, because a face small
                                        // enough to fit six of them is not a
                                        // face any more.
                                        <div
                                            className="absolute bottom-3 right-3 flex max-w-[calc(100%-1.5rem)] gap-2 overflow-x-auto"
                                            data-testid="stage-rail"
                                        >
                                            {plan.rail.map((entry) => (
                                                /*
                                                 * A button, not a tile in a
                                                 * box: pressing somebody puts
                                                 * them on the stage and holds
                                                 * them there, which is how
                                                 * you watch the partner who
                                                 * is not the one talking. The
                                                 * name is what it is called,
                                                 * because "tile" means
                                                 * nothing read aloud.
                                                 */
                                                <button
                                                    type="button"
                                                    key={entry.key}
                                                    onClick={() =>
                                                        setPinnedKey(entry.key)
                                                    }
                                                    aria-label={t("meetingLayout.pin", {
                                                        name:
                                                            entry.track.participant
                                                                .name ||
                                                            entry.track.participant
                                                                .identity ||
                                                            t("meetingLayout.someone"),
                                                    })}
                                                    className={`aspect-video w-28 shrink-0 overflow-hidden rounded-lg border shadow-lg sm:w-40 ${
                                                        entry.isSpeaking
                                                            ? "border-room-accent-ink"
                                                            : "border-room-line"
                                                    }`}
                                                    data-testid={
                                                        entry.isLocal &&
                                                        !entry.isScreenShare
                                                            ? "self-tile"
                                                            : undefined
                                                    }
                                                >
                                                    <ParticipantTile
                                                        trackRef={entry.track}
                                                        className="pointer-events-none size-full"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            <MeetingAudioBadge />

                            {alone && (
                                <div
                                    className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center"
                                    data-testid="meeting-waiting"
                                >
                                    <p
                                        role="status"
                                        className="flex items-center gap-2 rounded-full border border-room-line bg-room-base/85 px-3.5 py-1.5 text-xs font-medium text-room-ink-muted backdrop-blur-sm"
                                    >
                                        <Loader2
                                            size={13}
                                            className="animate-spin"
                                            aria-hidden="true"
                                        />
                                        {t("meetingControls.waitingForOthers")}
                                    </p>
                                </div>
                            )}

                        </div>
                    )}

                    {/*
                      * One instance, moved rather than remounted: remounting
                      * it between choosing and playing would stop the
                      * meditation it is playing.
                      *
                      * Neither state covers the video. Choosing is a short
                      * list, so it sits over the lower edge; a script being
                      * read is a column down the right, because a therapist
                      * reading aloud is reading *to* somebody and a prompter
                      * across the picture takes that somebody off the screen.
                      * On a narrow screen the column would leave neither
                      * usable, so it goes back to the lower edge.
                      */}
                    {showMeditation && (
                        <div
                            className={`pointer-events-none absolute z-10 flex [&>*]:pointer-events-auto ${
                                meditationRunning
                                    ? "inset-x-2 bottom-2 justify-center sm:inset-y-2 sm:left-auto sm:right-2 sm:w-80 sm:items-stretch"
                                    : "inset-x-2 bottom-2 justify-center"
                            }`}
                        >
                            <MeetingMeditation
                                channel={meditation}
                                onClose={() => setMeditationOpen(false)}
                                onRunningChange={setMeditationRunning}
                                // The same bowl the bar rings, sounded by the
                                // script rather than by hand — one strike,
                                // never the therapist's timed plan, whose
                                // closing marks would land long after the
                                // meditation had ended.
                                onGong={gong.canRing ? gong.ringOnce : undefined}
                                onMeditationEnd={gong.canRing ? gong.stop : undefined}
                            />
                        </div>
                    )}

                    {showBilateralPanel && (
                        <div
                            className="pointer-events-none absolute inset-x-2 bottom-2 z-10 flex justify-center sm:inset-y-2 sm:left-auto sm:right-2 sm:w-80 sm:items-stretch [&>*]:pointer-events-auto"
                        >
                            <BlsControlPanel channel={bls} onClose={() => setBilateralOpen(false)} recorder={emdrRecorder} />
                        </div>
                    )}
                </div>

                {chat.open && <MeetingChat channel={chat} />}
            </div>

            <MeetingBar
                board={board}
                chat={chat}
                fullscreen={fullscreen}
                gong={gong}
                layout={layout}
                canLeave={canLeave}
                compact={compact}
            />

            <RoomAudioRenderer />
        </div>
    );
}
